// frontend/src/App.js
import React, { useState, useEffect, useCallback, Suspense, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';

import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

import './App.css';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import CategorySection from './components/CategorySection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import BackToTopButton from './components/BackToTopButton';
import FeaturedProductsCarousel from './components/FeaturedProductsCarousel'; // Existing
import ComingSoonCarousel from './components/ComingSoonCarousel'; // NEW IMPORT
import WhyChooseUs from './components/WhyChooseUs';
import WhereToBuySection from './components/WhereToBuySection';
import WhatsAppButton from './components/WhatsAppButton';
import MobileNavBar from './components/MobileNavBar';

const LazyProductSection = React.lazy(() => import('./components/ProductSection'));
const LazySpiceDetailPage = React.lazy(() => import('./components/SpiceDetailPage'));
const LazySearchResultsPage = React.lazy(() => import('./components/SearchResultsPage'));
const LazyCategoryProductsPage = React.lazy(() => import('./components/CategoryProductsPage'));
const LazyLogin = React.lazy(() => import('./components/Login'));
const LazySpiceList = React.lazy(() => import('./components/SpiceList'));
const LazyAddSpice = React.lazy(() => import('./components/AddSpice'));
const LazyChangePassword = React.lazy(() => import('./components/ChangePassword'));
const LazyReturnPolicy = React.lazy(() => import('./components/ReturnPolicy'));
const LazyTermsAndPrivacy = React.lazy(() => import('./components/TermsAndPrivacy')); // adjust path if needed

const PrivateRoute = ({ children, isAuthenticated }) => {
    return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

function App() {
    // ... (Authentication state logic)
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
    const [currentUsername, setCurrentUsername] = useState(localStorage.getItem('username') || '');

    const [spices, setSpices] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshPublicSpices, setRefreshPublicSpices] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();

    // ... (Mobile Menu Logic)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const mobileSearchInputRef = useRef(null);

    const toggleMobileMenu = () => {
        if (!isMobileMenuOpen) {
            setIsMobileSearchActive(false); 
        }
        setIsMobileMenuOpen(prevState => !prevState);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const handleMobileSearchToggle = (activate) => {
        setIsMobileSearchActive(activate);
        if (activate) {
            if (isMobileMenuOpen) {
                setIsMobileMenuOpen(false); 
            }
            setTimeout(() => {
                mobileSearchInputRef.current?.focus();
            }, 100);
        } else {
            setSearchTerm(''); 
        }
    };
    
    // ... (Fetch Data Logic)
    const fetchPublicSpicesData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch('https://greenpod-project-backend.onrender.com/api/spices');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setSpices(data); 
            setError(null); 
        } catch (err) {
            console.error("Error fetching public spice data:", err);
            setError("Failed to fetch spices for public display. Please ensure the backend server is running.");
        } finally {
            setLoading(false); 
        }
    }, []);

    useEffect(() => {
        fetchPublicSpicesData();
    }, [fetchPublicSpicesData, refreshPublicSpices]);

    useEffect(() => {
        setCurrentUsername(localStorage.getItem('username') || '');
    }, [isAuthenticated]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setIsAuthenticated(false);
        setCurrentUsername('');
        navigate('/', { replace: true }); 
    };

    const showPublicLayout = !location.pathname.startsWith('/admin');

    const handleSearch = useCallback((searchTerm) => {
        if (searchTerm) {
            navigate(`/search-results?query=${encodeURIComponent(searchTerm)}`);
        } else {
            navigate(`/search-results`); 
        }
    }, [navigate]);

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location]);

    useEffect(() => {
        if (!location.hash) {
            window.scrollTo(0, 0);
        }
    }, [location.pathname]);

    const WHATSAPP_PHONE_NUMBER = '+447436592175';
    const WHATSAPP_DEFAULT_MESSAGE = 'Hello, I saw your website and would like to inquire about your spices.';

    return (
        <div className="App">
            {showPublicLayout && (
                <Navbar
                    onSearch={handleSearch}
                    isMobileMenuOpen={isMobileMenuOpen}
                    toggleMobileMenu={toggleMobileMenu}
                    closeMobileMenu={closeMobileMenu}
                    isMobileSearchActive={isMobileSearchActive}
                    handleMobileSearchToggle={handleMobileSearchToggle}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    mobileSearchInputRef={mobileSearchInputRef}
                    allSpices={spices} 
                />
            )}

            {showPublicLayout && (
                <div className={`menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}></div>
            )}
            {showPublicLayout && (
                <div className={`mobile-nav-container ${isMobileMenuOpen ? 'open' : ''}`}>
                   {/* Mobile menu content */}
                   <div className="mobile-menu-header">
                        <h3>Menu</h3>
                        <FaTimes className="close-menu-icon" onClick={closeMobileMenu} />
                    </div>
                    <ul className="mobile-nav-list">
                        <li><Link to="/#categories-section" onClick={closeMobileMenu}>CATEGORIES</Link></li>
                        <li><Link to="/products" onClick={closeMobileMenu}>ALL PRODUCTS</Link></li>
                       
                        <li><Link to="/products/The%20Chai%20Lovers" onClick={closeMobileMenu}>THE CHAI LOVERS</Link></li>
                        <li><Link to="/#about-section" onClick={closeMobileMenu}>ABOUT US</Link></li>
                        <li><Link to="/#contact-section" onClick={closeMobileMenu}>CONTACT US</Link></li>
                    </ul>
                </div>
            )}
            {showPublicLayout && (
                <MobileNavBar
                    whatsappPhoneNumber={WHATSAPP_PHONE_NUMBER}
                    whatsappMessage={WHATSAPP_DEFAULT_MESSAGE}
                />
            )}

            <Suspense fallback={
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', fontSize: '1.2rem', color: '#666' }}>
                    Loading page content...
                </div>
            }>
                <Routes>
                    <Route path="/" element={
                        <>
                            {showPublicLayout && (
                                <>
                                    <HeroSection />
                                </>
                            )}
                            <main>
                                <CategorySection />
                                
                                {/* 1. The Active/Featured Spices Carousel (Buyable) */}
                                <FeaturedProductsCarousel publicSpices={spices} />

                                {/* 2. The Coming Soon Carousel (Old/Future items, No Buy Button) */}
                                <ComingSoonCarousel publicSpices={spices} />
                                
                                <WhyChooseUs />
                                <WhereToBuySection 
                                    amazonUrl="https://amzn.eu/d/0bFPHGZe" 
                                    ebayUrl="https://ebay.us/m/64zYbu"
                                />
                                <AboutSection />
                                <ContactSection />
                            </main>
                        </>
                    } />

                    {/* ... (Rest of routes) ... */}
                    <Route path="/products" element={<div style={{ paddingTop: '100px', minHeight: '80vh', marginBottom: '50px' }}><LazyProductSection publicSpices={spices} publicLoading={loading} publicError={error} /></div>} />
                    <Route path="/search-results" element={<LazySearchResultsPage allSpices={spices} loading={loading} error={error} />} />
                    <Route path="/products/:categoryName" element={<LazyCategoryProductsPage />} />
                    <Route path="/product/:id" element={<LazySpiceDetailPage />} />
                    <Route path="/return-policy" element={<LazyReturnPolicy />} />
                    <Route path="/terms-and-privacy" element={<LazyTermsAndPrivacy />} />

                    
                    <Route path="/admin/login" element={isAuthenticated ? <Navigate to="/admin/spices" /> : <LazyLogin setIsAuthenticated={setIsAuthenticated} />} />
                    <Route path="/admin/spices" element={<PrivateRoute isAuthenticated={isAuthenticated}><LazySpiceList handleLogout={handleLogout} currentUsername={currentUsername} /></PrivateRoute>} />
                    <Route path="/admin/add-spice/:id" element={<PrivateRoute isAuthenticated={isAuthenticated}><LazyAddSpice setRefreshPublicSpices={() => setRefreshPublicSpices(prev => prev + 1)} /></PrivateRoute>} />
                    <Route path="/admin/add-spice" element={<PrivateRoute isAuthenticated={isAuthenticated}><LazyAddSpice setRefreshPublicSpices={() => setRefreshPublicSpices(prev => prev + 1)} /></PrivateRoute>} />
                    <Route path="/admin/change-password" element={<PrivateRoute isAuthenticated={isAuthenticated}><LazyChangePassword /></PrivateRoute>} />
                    <Route path="*" element={<h2 style={{ textAlign: 'center', marginTop: '50px' }}>404 - Page Not Found</h2>} />
                </Routes>
            </Suspense>

            {showPublicLayout && <Footer />}
            {showPublicLayout && <BackToTopButton />}
            {showPublicLayout && (
                <WhatsAppButton phoneNumber={WHATSAPP_PHONE_NUMBER} message={WHATSAPP_DEFAULT_MESSAGE} />
            )}
            <Analytics />
            <SpeedInsights />
        </div>
    );
}

export default App;